
(() => {
  "use strict";

  /*
   * DUODEX DIRECT PRE-ORDER
   *
   * - Supports main product and upsell buttons
   * - Adds directly to Shopify cart
   * - Requires an unambiguous selling plan
   * - Verifies the cart after adding
   * - Refreshes the page to update theme cart data
   * - Opens the theme's native cart trigger
   * - Does not invoke the PreProduct popup
   */

  const root = window.Shopify?.routes?.root || "/";
  const selector = "[data-duodex-direct-preorder]";
  const pendingCartKey = "duodex-open-cart-after-add";

  // ------------------------------------------
  // NOTIFICATIONS
  // ------------------------------------------

  const notice = (message, error = false) => {
    document
      .querySelector("#duodex-preorder-notice")
      ?.remove();

    const box = document.createElement("div");

    box.id = "duodex-preorder-notice";
    box.setAttribute("role", "status");

    box.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 20px;
      z-index: 999999;
      background: ${error ? "#fff0f0" : "#eaf8ff"};
      color: #18252a;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 3px 18px #0002;
      max-width: 340px;
      font: 14px Arial, sans-serif;
    `;

    box.textContent = message;

    if (!error) {
      const link = document.createElement("a");
      link.href = root + "cart";
      link.textContent = "Vai al carrello";

      link.style.cssText = `
        display: block;
        margin-top: 8px;
        color: #126293;
        font-weight: bold;
      `;

      box.appendChild(link);
    }

    document.body.appendChild(box);
  };

  // ------------------------------------------
  // OPEN THE NATIVE SHOPIFY CART
  // ------------------------------------------

  const openNativeCart = () => {
    /*
     * Use the theme's existing cart button.
     *
     * Different Shopify themes use different
     * selectors. The native button is preferred
     * because it already knows how to open
     * and close its own cart drawer.
     */

    const selectors = [
      'header [data-drawer-toggle][aria-controls*="cart" i]',
      '[data-drawer-toggle][aria-controls*="cart" i]',
      'header [data-cart-drawer-toggle]',
      'header [data-cart-toggle]',
      'header [aria-controls="CartDrawer"]',
      'header [aria-controls="cart-drawer"]',
      '[data-header] [data-cart-toggle]',
      '[data-header] [data-cart-drawer-toggle]',
      'header a[href$="/cart"]',
      '[data-header] a[href$="/cart"]',
      '.site-header a[href$="/cart"]',
      '.header a[href$="/cart"]',
      'header button[aria-label*="cart" i]',
      'header a[aria-label*="cart" i]',
      'a[href$="/cart"]'
    ];

    for (const query of selectors) {
      const elements = document.querySelectorAll(query);

      for (const element of elements) {
        if (element.getClientRects().length === 0) {
          continue;
        }

        console.log(
          "[DuoDex] Opening native cart:",
          element
        );

        element.click();
        return true;
      }
    }

    return false;
  };

  // ------------------------------------------
  // RESTORE CART AFTER PAGE REFRESH
  // ------------------------------------------

  const restoreCart = () => {
    let shouldOpen = false;

    try {
      shouldOpen =
        sessionStorage.getItem(pendingCartKey) === "1";

      if (shouldOpen) {
        sessionStorage.removeItem(pendingCartKey);
      }
    } catch (error) {
      console.warn(
        "[DuoDex] Session storage unavailable:",
        error
      );
    }

    if (!shouldOpen) return;

    /*
     * Allow the theme to initialize its
     * header and cart components.
     */

    setTimeout(() => {
      const opened = openNativeCart();

      if (!opened) {
        console.warn(
          "[DuoDex] Native cart trigger not found."
        );

        // Safe fallback: show the actual Shopify cart
        window.location.assign(root + "cart");
      }
    }, 450);
  };

  if (document.readyState === "complete") {
    restoreCart();
  } else {
    window.addEventListener(
      "load",
      restoreCart,
      { once: true }
    );
  }

  // ------------------------------------------
  // HANDLE DIRECT PRE-ORDER BUTTONS
  // ------------------------------------------

  window.addEventListener(
    "click",
    async (event) => {
      const button = event.target.closest?.(selector);

      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        button.disabled ||
        button.dataset.loading === "true"
      ) {
        return;
      }

      const handle = button.dataset.duodexHandle;
      const form = button.closest("form");

      // Correct variant for main product or upsell
      const variantId = String(
        form?.querySelector('[name="id"]')?.value ||
        button.dataset.duodexVariant ||
        ""
      );

      // Quantity from the relevant product form
      const quantity = Number(
        form?.querySelector('[name="quantity"]')?.value || 1
      );

      if (
        !handle ||
        !variantId ||
        !Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        quantity > 10
      ) {
        notice(
          "Dati del prodotto non validi.",
          true
        );
        return;
      }

      button.dataset.loading = "true";
      button.disabled = true;

      const text = button.querySelector(".btn__text");
      const oldText = text?.textContent;

      if (text) {
        text.textContent = "Adding...";
      }

      let added = false;

      try {
        // ----------------------------------
        // GET PRODUCT DATA
        // ----------------------------------

        const productResponse = await fetch(
          root +
            "products/" +
            encodeURIComponent(handle) +
            ".js",
          {
            credentials: "same-origin"
          }
        );

        if (!productResponse.ok) {
          throw new Error(
            "Impossibile leggere il prodotto."
          );
        }

        const product = await productResponse.json();

        const variant = product.variants.find(
          v => String(v.id) === variantId
        );

        if (!variant) {
          throw new Error("Variante non trovata.");
        }

        // ----------------------------------
        // VALIDATE PRE-ORDER SELLING PLAN
        // ----------------------------------

        const plans =
          variant.selling_plan_allocations || [];

        /*
         * Prevent accidental regular orders.
         * An ambiguous or missing plan blocks
         * the purchase.
         */

        if (
          plans.length !== 1 ||
          !plans[0].selling_plan_id
        ) {
          throw new Error(
            "Piano PreProduct assente o ambiguo. Acquisto bloccato."
          );
        }

        const planId = plans[0].selling_plan_id;

        // ----------------------------------
        // ADD TO SHOPIFY CART
        // ----------------------------------

        const payload = {
          items: [
            {
              id: Number(variantId),
              quantity,
              selling_plan: planId,
              properties: {
                _preorder: "true",
                "Estimated shipping": "40 days"
              }
            }
          ]
        };

        const response = await fetch(
          root + "cart/add.js",
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) {
          const error = await response.text();

          throw new Error(
            "Shopify: " + error.slice(0, 200)
          );
        }

        added = true;

        // ----------------------------------
        // VERIFY SHOPIFY CART
        // ----------------------------------

        const cartResponse = await fetch(
          root + "cart.js",
          {
            credentials: "same-origin",
            cache: "no-store"
          }
        );

        if (!cartResponse.ok) {
          throw new Error(
            "Impossibile verificare il carrello."
          );
        }

        const cart = await cartResponse.json();

        const line = cart.items.find(
          item =>
            String(item.variant_id) === variantId &&
            String(
              item.selling_plan_allocation
                ?.selling_plan?.id
            ) === String(planId) &&
            item.properties?._preorder === "true"
        );

        if (!line) {
          throw new Error(
            "Articolo aggiunto, ma preordine non verificato."
          );
        }

        console.log(
          "[DuoDex] Pre-order added:",
          {
            product: product.title,
            variantId,
            quantity,
            sellingPlan: planId,
            cartCount: cart.item_count
          }
        );

        // ----------------------------------
        // OPEN THEME CART AFTER REFRESH
        // ----------------------------------

        /*
         * The page refresh makes Shopify's
         * Liquid cart count and drawer content
         * reflect the newly added item.
         *
         * On reload, restoreCart() clicks the
         * native header cart button.
         */

        sessionStorage.setItem(
          pendingCartKey,
          "1"
        );

        window.location.reload();

      } catch (error) {
        console.error(
          "[DuoDex Pre-order]",
          error
        );

        notice(
          added
            ? "L'articolo potrebbe essere già stato aggiunto. Controlla il carrello prima di riprovare."
            : "Preordine non aggiunto: " +
                error.message,
          true
        );

        button.disabled = false;
        button.dataset.loading = "false";

        if (text) {
          text.textContent = oldText;
        }
      }
    },
    true
  );
})();
